// src/pages/HomePage.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/Navbar";
import Quotes from "../components/Quotes";
import Calendar from "../components/Calendar";
import { api } from "../api";
import "../App.css";
import "./HomePage.css";

import avatarMale from "../assets/avatars/avatar_male.png";
import avatarFemale from "../assets/avatars/avatar_female.png";

/* ---- week helpers ---- */
const monIdx = (jsDay) => (jsDay + 6) % 7; // Mon=0..Sun=6

function startOfWeek(date) {
  const d = new Date(date);
  const w = monIdx(d.getDay());
  d.setDate(d.getDate() - w);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ✅ same helper as ProfilePage (key -> image src)
function loadAvatarMap() {
  const req = require.context("../assets/avatars", false, /\.(png|jpe?g|svg|webp)$/i);
  const map = {};
  req.keys().forEach((p) => {
    const mod = req(p);
    const src = mod?.default ?? mod;
    const filename = p.replace("./", "");
    const key = filename.replace(/\.[^.]+$/, "");
    map[key] = src;
  });
  return map;
}

// ✅ Home: NO blur. Other pages: blur (default).

export default function HomePage({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const profile = user?.profile || {};
  const sex = (profile.sex || "").toLowerCase();

  // ✅ avatarKey from DB
  const avatarKey = profile.avatarKey || "";

  // fallback if no avatarKey yet
  const fallbackAvatarSrc = sex === "female" ? avatarFemale : avatarMale;

  // ✅ build map once
  const avatarMap = useMemo(() => loadAvatarMap(), []);

  // ✅ prefer avatarKey, fallback to sex default
  const avatarSrc = avatarKey && avatarMap[avatarKey] ? avatarMap[avatarKey] : fallbackAvatarSrc;

  const name = profile.firstName || user?.nickname || user?.email || t("common.friend");

  // ✅ load plan schedule once for weekly preview
  const [schedule, setSchedule] = useState(null);

  useEffect(() => {
    document.body.classList.add("no-wallpaper-blur");
    return () => document.body.classList.remove("no-wallpaper-blur");
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const prefs = await api("/me/preferences", { cacheTtlMs: 5 * 60_000 });
        if (!mounted) return;

        if (!prefs?.goal) {
          setSchedule(null);
          return;
        }

        const planData = await api("/me/plan/schedule", { cacheTtlMs: 5 * 60_000 });
        if (!mounted) return;

        setSchedule(planData?.schedule ?? null);
      } catch (err) {
        console.error("[HomePage] Failed to load plan schedule", err);
        if (mounted) setSchedule(null);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // ✅ build marked map for "this week" only
  const marked = useMemo(() => {
    const map = new Map();
    if (!schedule) return map;

    const s = startOfWeek(new Date());
    for (let i = 0; i < 7; i++) {
      const d = new Date(s.getFullYear(), s.getMonth(), s.getDate() + i);
      const ymd = toYMD(d);
      const entry = schedule?.[ymd];

      map.set(ymd, {
        count: entry?.exercises?.length || 0,
        type: entry?.dayType || "rest",
        confirmed: false,
      });
    }

    return map;
  }, [schedule]);

  const todayYmd = useMemo(() => toYMD(new Date()), []);

  const handleDaySelect = (ymd) => {
    navigate(`/plan?day=${encodeURIComponent(ymd)}`);
  };

  return (
    <>
      <Navbar user={user} onLogout={onLogout} tab="home" hideAvatar={false} />

      <div className="page-wrapper home-bg">
        <div className="page-container">
          <main className="home-page">
            <section className="home-panel">
              <div className="home-content">
                <div className="home-module home-welcome glass">
                  <div className="welcome-left">
                    <h1>
                      {t("home.welcome", { name })} 👋
                    </h1>
                  </div>

                  <div className="welcome-right">
                    <img src={avatarSrc} alt={t("home.avatarAlt")} className="welcome-avatar" />
                  </div>
                </div>

                <div className="home-module glass">
                  <h2>{t("home.dailyQuoteTitle")}</h2>
                  <div className="quote-wrapper">
                    <Quotes />
                  </div>
                </div>

                <div className="home-module glass">
                  <h2>{t("home.nextWorkoutTitle")}</h2>
                  <div className="calendar-wrapper">
                    <Calendar
                      mode="week"
                      marked={marked}
                      selectedYmd={todayYmd}
                      onDaySelect={handleDaySelect}
                    />
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
    </>
  );
}